/**
 * normalizeToolbarIcons — uniform DRAWN size for the top-row + left-rail icons.
 *
 * Every icon button already forces its <svg> to a 21px box, but each glyph fills
 * its own viewBox by a different amount (some use a 0 0 16 16 viewBox, some
 * 0 0 24 24, and the drawn shapes span anywhere from ~55% to ~80% of it). So at
 * the same 21px box they render at visibly different drawn sizes (~13–18px).
 *
 * This measures each icon's actual content bounds (getBBox) and rewrites the
 * svg's viewBox to a SQUARE centred on that content, sized so the content's
 * max dimension is a fixed fraction (TARGET_FRAC) of the box. Result: every
 * glyph's largest dimension fills the same fraction → one uniform drawn size,
 * regardless of the source viewBox or how tightly the artwork was drawn.
 *
 * Text-based icons (<text> glyphs) are skipped — getBBox on <text> depends on
 * font metrics and load timing. The little dropdown chevrons (.markup-tb-chev)
 * are excluded too.
 *
 * A LETTERFORM drawn as paths (the Text Box "T", the Redact "R") reads larger
 * than a shape of the same extent, so pulling it up to the common fraction
 * makes it look oversized beside the rectangle — and shrinking its path does
 * nothing, because the normaliser scales it straight back up. Such an icon
 * carries `data-icon-frac="0.55"` (its own fraction of the box) and is
 * normalised to that instead of TARGET_FRAC.
 *
 * Idempotent + self-healing: React re-renders reset the viewBox from JSX, so a
 * MutationObserver re-applies the normalised viewBox whenever that happens. The
 * stored value guard makes re-setting the same value a no-op, so there's no
 * observer feedback loop.
 */

// Direct-child icon svgs of the toolbar/rail buttons (not nested chevrons).
const SELECTOR = [
  '.markup-tb-btn > svg:not(.markup-tb-chev)',
  '.twin-tb-btn > svg',
  '.pidly-left-rail .left-rail-btn > svg',
  '.menu-bar-burger > svg',
  '.back-btn > svg',
].join(', ');

// Content max-dimension as a fraction of the (square) box. 0.75 ≈ the current
// median fill (a 12-unit glyph in a 16 viewBox), so the majority of icons don't
// move — only the small/large outliers are pulled into line.
const TARGET_FRAC = 0.75;
const KEY = '__iconNormVB';

function normalizeOne(svg) {
  // Skip text glyphs (font-metric dependent) and anything without a viewBox.
  if (!svg || svg.querySelector('text')) return;
  const vb = svg.viewBox && svg.viewBox.baseVal;
  if (!vb || !vb.width || !vb.height) return;
  let bbox;
  try { bbox = svg.getBBox(); } catch { return; }
  if (!bbox || (!bbox.width && !bbox.height)) return; // not laid out yet
  const maxDim = Math.max(bbox.width, bbox.height);
  if (!maxDim) return;
  const ownFrac = parseFloat(svg.getAttribute('data-icon-frac'));
  const frac = ownFrac > 0 && ownFrac <= 1 ? ownFrac : TARGET_FRAC;
  const side = maxDim / frac;
  const cx = bbox.x + bbox.width / 2;
  const cy = bbox.y + bbox.height / 2;
  const nvb = `${(cx - side / 2).toFixed(2)} ${(cy - side / 2).toFixed(2)} ${side.toFixed(2)} ${side.toFixed(2)}`;
  // Already normalised to this exact value → no-op (prevents observer loop).
  if (svg[KEY] === nvb && svg.getAttribute('viewBox') === nvb) return;
  svg[KEY] = nvb;
  svg.setAttribute('viewBox', nvb);
}

let scheduled = false;
function runSoon() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    document.querySelectorAll(SELECTOR).forEach(normalizeOne);
  });
}

export function initToolbarIconNormalizer() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (window.__iconNormInit) return;
  window.__iconNormInit = true;

  runSoon();

  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList' && m.addedNodes.length) { runSoon(); return; }
      if (m.type === 'attributes' && m.attributeName === 'viewBox') {
        // A viewBox we manage got reset by React → re-normalise it.
        const t = m.target;
        if (t && t[KEY] && t.getAttribute('viewBox') !== t[KEY]) { runSoon(); return; }
      }
    }
  });
  obs.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['viewBox'],
  });
}
